import { FC, useCallback, useMemo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { createColumnHelper, ColumnDef, Row } from '@tanstack/react-table';
import {
  Button,
  DataTableSkeleton,
  InlineNotification,
  Link,
  RadioButton,
  RadioButtonGroup,
  Tag,
} from '@carbon/react';
import { Play } from '@carbon/react/icons';
import { PageHeader } from 'components/page-header';
import { StatsBar } from 'components/stats-bar';
import { DataTable } from 'components/data-table';
import { formatNumber } from 'utils/number';
import { parseUTCdateTime } from 'utils/dates';
import {
  fetchCategoriesPhase,
  fetchSlotsPhase,
  kindAtom,
  runAuditAtom,
  runAuditLoadableAtom,
} from './atoms';
import { SlotDetail } from './slot-detail';
import {
  CATEGORY_LABELS,
  interestPercent,
  mergeVerdicts,
  OVERSIZED_GEOMETRY_MULTIPLE,
} from './utils';
import { useBackgroundAudit } from '../shared/use-background-audit';
import type {
  AuditStatus,
  OverAllocatedSlot,
  OverAllocatedSlotsStats,
  SlotKindFilter,
} from './types';
import styles from './page.module.scss';

const TABLE_CONFIG = {
  sorting: true,
  globalSearch: true,
  columnFilters: true,
  columnResizing: true,
  columnReordering: true,
  columnPinning: true,
  columnVisibility: true,
} as const;

const columnHelper = createColumnHelper<OverAllocatedSlot>();

const columns = [
  columnHelper.accessor('kind', {
    id: 'kind',
    header: 'Grain',
    size: 100,
    cell: (info) => (
      <Tag type={info.getValue() === 'well' ? 'blue' : 'teal'} size="sm">
        {info.getValue() === 'well' ? 'Well' : 'LTD'}
      </Tag>
    ),
  }),
  columnHelper.accessor('unitName', {
    id: 'unitName',
    header: 'Unit',
    size: 170,
    cell: (info) => (
      <Link href={`/development_areas/${info.row.original.developmentAreaId}`}>
        {info.getValue()}
      </Link>
    ),
  }),
  // One column for both grains: an LTD slot is identified by its formation and
  // slot number, a well slot by its UWI and lease name.
  columnHelper.accessor(
    (row) => {
      if (row.kind === 'ltd') {
        return `${row.formationName ?? '--'} #${row.slotNumber ?? '--'}`;
      }
      return row.wellName ?? row.wellUWI ?? '--';
    },
    {
      id: 'slotLabel',
      header: 'Slot',
      size: 230,
    }
  ),
  columnHelper.accessor('wellUWI', {
    id: 'wellUWI',
    header: 'UWI',
    size: 150,
    cell: (info) => info.getValue() ?? '--',
  }),
  columnHelper.accessor('sumNri', {
    id: 'sumNri',
    header: 'Σ NRI (%)',
    size: 150,
    cell: (info) => (
      <span className={info.getValue() > 1 ? styles.overOne : undefined}>
        {interestPercent(info.getValue())}
      </span>
    ),
  }),
  columnHelper.accessor('sumWi', {
    id: 'sumWi',
    header: 'Σ WI (%)',
    size: 150,
    cell: (info) => (
      <span className={info.getValue() > 1 ? styles.overOne : undefined}>
        {interestPercent(info.getValue())}
      </span>
    ),
  }),
  columnHelper.accessor('excess', {
    id: 'excess',
    header: 'Excess',
    size: 120,
    cell: (info) =>
      `+${formatNumber(info.getValue() * 100, 1, 1, 'decimal', true)}%`,
  }),
  columnHelper.accessor('subtractCount', {
    id: 'subtractCount',
    header: 'Subtracts',
    size: 110,
    meta: { type: 'integer' },
  }),
  // Why the row is here. The sums beside it are across every owner on the slot;
  // this is the one that crossed the line on its own.
  columnHelper.accessor('worstAccount', {
    id: 'worstAccount',
    header: 'Over-Claiming Account',
    size: 240,
    cell: (info) => info.getValue() ?? '--',
  }),
  columnHelper.accessor('worstAccountNri', {
    id: 'worstAccountNri',
    header: 'Account Σ NRI (%)',
    size: 150,
    cell: (info) => interestPercent(info.getValue()),
  }),
  columnHelper.accessor('worstAccountWi', {
    id: 'worstAccountWi',
    header: 'Account Σ WI (%)',
    size: 150,
    cell: (info) => interestPercent(info.getValue()),
  }),
  columnHelper.accessor('rowCount', {
    id: 'rowCount',
    header: 'Calculations',
    size: 120,
    meta: { type: 'integer' },
  }),
  // Which known mechanism the slot's evidence points at. Both can apply. An
  // empty verdict is a finding — neither mechanism explains this slot — and is
  // labelled as such rather than left blank, which would read as "not checked".
  columnHelper.accessor('categories', {
    id: 'categories',
    header: 'Category',
    size: 260,
    cell: (info) => {
      const keys = info.getValue();
      // Absent until the categories phase lands, and null when it failed —
      // both mean "not known", as distinct from an empty verdict below.
      if (!keys) return '--';
      if (keys.length === 0) {
        return (
          <Tag type="cool-gray" size="sm">
            unexplained
          </Tag>
        );
      }
      return keys.map((key) => (
        <Tag key={key} type={CATEGORY_LABELS[key].tag} size="sm">
          {CATEGORY_LABELS[key].short}
        </Tag>
      ));
    },
    // Sort by mechanism so a triage pass can group the two populations, with
    // unexplained slots last rather than interleaved.
    sortingFn: (a, b) => {
      const rank = (row: Row<OverAllocatedSlot>) => {
        const keys = row.original.categories;
        if (!keys) return 3;
        if (keys.length === 0) return 2;
        return keys.length > 1 ? 0 : 1;
      };
      return rank(a) - rank(b);
    },
  }),
  columnHelper.accessor('allocationMethod', {
    id: 'allocationMethod',
    header: 'Allocation Method',
    size: 160,
  }),
  columnHelper.accessor('lastCalculatedAt', {
    id: 'lastCalculatedAt',
    header: 'Last Calculated',
    size: 180,
    cell: (info) => {
      const value = info.getValue();
      // UTC, like the rest of the app. toLocaleString() rendered these in the
      // reader's zone, so the same record read differently depending on who
      // opened it — and these timestamps are compared across records to spot a
      // common load.
      return value ? parseUTCdateTime(value, true) : '--';
    },
  }),
];

/**
 * Narrows the listed slots to a mechanism. Chips OR together, so selecting both
 * widens rather than narrows — a slot can carry both at once, and ANDing them
 * would read as "carries both" where a reader means "either".
 *
 * `unexplained` is a classified verdict of nothing, which is NOT the same as a
 * slot not yet classified: one is a finding, the other is a phase that has not
 * landed. Rows still awaiting classification match no chip and drop out while
 * any chip is selected, which is the honest behaviour — nothing is yet known
 * about them.
 */
const CATEGORY_FILTERS: {
  key: string;
  label: string;
  tagType: 'red' | 'magenta' | 'cool-gray';
  matches: (row: OverAllocatedSlot) => boolean;
}[] = [
  {
    key: 'understated_gross',
    label: 'Understated Gross',
    tagType: 'red',
    matches: (row) => !!row.categories?.includes('understated_gross'),
  },
  {
    key: 'duplicate_parcel',
    label: 'Duplicate Parcel',
    tagType: 'magenta',
    matches: (row) => !!row.categories?.includes('duplicate_parcel'),
  },
  {
    key: 'unexplained',
    label: 'Unexplained',
    tagType: 'cool-gray',
    matches: (row) =>
      Array.isArray(row.categories) && row.categories.length === 0,
  },
];

/**
 * What the button says while an audit is in flight. Queued and running are named
 * apart on purpose — a long wait for a worker is not a slow query, and reading
 * "Running…" through both makes it look like one.
 */
const BUTTON_LABELS: Partial<Record<AuditStatus, string>> = {
  queued: 'Queued…',
  running: 'Running…',
  slots_ready: 'Classifying…',
};

const renderRowSubComponent = (row: Row<OverAllocatedSlot>) => (
  <SlotDetail slot={row.original} />
);

export const OverAllocatedSlotsPage: FC = () => {
  const [kind, setKind] = useAtom(kindAtom);
  const runAudit = useSetAtom(runAuditAtom);
  const runState = useAtomValue(runAuditLoadableAtom);

  const [slots, setSlots] = useState<OverAllocatedSlot[]>([]);
  const [stats, setStats] = useState<OverAllocatedSlotsStats | null>(null);
  const [capped, setCapped] = useState(false);
  const [phaseErrors, setPhaseErrors] = useState<Record<string, string>>({});
  const [activeCategories, setActiveCategories] = useState<Set<string>>(
    new Set()
  );

  // Both loaders answer whether their phase had landed; the lifecycle reads that
  // and moves the status. Slots advance from `queued` too — without Pusher there
  // is no audit_started, so the payload arriving is itself the evidence that
  // work has begun.
  const loadSlots = useCallback(async (id: string) => {
    const payload = await fetchSlotsPhase(id);
    if (!payload.ready) return false;
    setSlots(payload.data);
    setStats(payload.stats);
    setCapped(payload.capped);
    return true;
  }, []);

  // Merged onto rows already on screen rather than replacing them, so the table
  // does not flicker or lose the row a reader has expanded.
  const loadCategories = useCallback(async (id: string) => {
    const payload = await fetchCategoriesPhase(id);
    if (!payload.ready) return false;
    setSlots((prev) => mergeVerdicts(prev, payload.verdicts));
    // Classification is the last phase, so its arrival completes the audit and
    // stops the polling fallback even if audit_complete never reaches us.
    return true;
  }, []);

  const { status, elapsed, error } = useBackgroundAudit<AuditStatus>({
    runState,
    phases: [
      {
        event: 'slots_ready',
        load: loadSlots,
        status: 'slots_ready',
        onlyWhenPending: true,
      },
      { event: 'categories_ready', load: loadCategories, status: 'complete' },
    ],
    // Classification is still outstanding at `slots_ready`, so polling has to
    // outlive the first phase.
    isPolling: (current) =>
      current === 'queued' ||
      current === 'running' ||
      current === 'slots_ready',
    // A phase can fail without failing the audit: a classification timeout
    // leaves the slot list intact and reports the Category column as unknown.
    onSubscribe: (subscription) => {
      subscription.bind(
        'phase_error',
        (data: { phase: string; error: string }) =>
          setPhaseErrors((prev) => ({ ...prev, [data.phase]: data.error }))
      );
    },
    onStart: () => {
      setSlots([]);
      setStats(null);
      setCapped(false);
      setPhaseErrors({});
    },
  });

  const toggleCategory = useCallback((key: string) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }, []);

  const visibleSlots = useMemo(() => {
    const chosen = CATEGORY_FILTERS.filter((f) => activeCategories.has(f.key));
    if (chosen.length === 0) return slots;

    return slots.filter((row) => chosen.some((f) => f.matches(row)));
  }, [slots, activeCategories]);

  const isRunning =
    status === 'queued' || status === 'running' || status === 'slots_ready';
  // PageHeader collapses a labelled action button to a 2.7rem square below
  // screen-xlg, hiding the label via `span { display: none }` and centring the
  // icon. Carbon renders children bare, so an unwrapped label was never hidden —
  // it clipped inside the square. Wrapped, with an icon to show in its place,
  // and aria-label so the button keeps an accessible name once the span leaves
  // the a11y tree.
  const buttonLabel = (isRunning && BUTTON_LABELS[status]) || 'Run audit';
  const hasRun = status !== 'idle';

  return (
    <>
      <PageHeader
        breadcrumbs={[['/admin/dashboard', 'Admin']]}
        title="Over-Claimed Slots"
        actions={
          <Button
            kind="primary"
            size="sm"
            renderIcon={Play}
            iconDescription={buttonLabel}
            aria-label={buttonLabel}
            disabled={isRunning}
            onClick={() => runAudit(kind)}
          >
            <span>{buttonLabel}</span>
          </Button>
        }
      />
      <div className={styles.sectionHeader}>
        <div className={styles.sectionDescription}>
          Slots where a <strong>single owner</strong> claims more than the whole
          slot. Judged per account, not across the slot: two owners summing past
          100% between them is ordinary — the same ground changes hands over
          time and Salesforce keeps both records — so only a party past its own
          100% is listed.
        </div>
        <ul className={styles.legend}>
          <li>
            <strong>Over-Claiming Account</strong> — the owner that put the slot
            on the list. The <strong>Account Σ</strong> columns are that
            owner&apos;s; the <strong>Σ NRI</strong> and <strong>Σ WI</strong>{' '}
            before them are the slot&apos;s across every owner, for context.
          </li>
          <li>
            <strong>Understated Gross</strong> — a subtract&apos;s polygon is
            more than {OVERSIZED_GEOMETRY_MULTIPLE}x the acreage recorded for
            it. Gross acreage divides the interest, so an understated one
            inflates it.
          </li>
          <li>
            <strong>Duplicate Parcel</strong> — one account holds two or more
            records on the same polygon, claiming more than the whole of it
            between them. The same lease counted twice.
          </li>
          <li>
            <strong>Unexplained</strong> — over-claimed, but neither mechanism
            is present. Not the same as a slot that has not been classified yet.
          </li>
          <li>
            <strong>Both / LTD / Well</strong> — the two grains that reach a
            slot by different keys: <strong>LTD</strong> future-development
            slots, and <strong>Well</strong> slots keyed by UWI.
          </li>
        </ul>
        {/* The scan runs off the request because it outgrew the request budget,
            so the report is explicitly started rather than fetched on arrival. */}
        <div className={styles.sectionDescription}>
          Expand a row for the acreage chain, the estate breakdown and the
          parcels behind it. The roll-up scans every calculation and
          classification reaches subtract geometry for each one, so the table
          fills in as soon as the slots land and the Category column follows.
        </div>
        <div className={styles.kindFilter}>
          <RadioButtonGroup
            legendText="Show"
            name="over-allocated-slot-kind"
            valueSelected={kind}
            onChange={(value) => setKind(value as SlotKindFilter)}
          >
            <RadioButton labelText="Both" value="both" id="kind-both" />
            <RadioButton labelText="LTD slots" value="ltd" id="kind-ltd" />
            <RadioButton labelText="Well slots" value="well" id="kind-well" />
          </RadioButtonGroup>
        </div>
        {stats && stats.totalSlots > 0 && (
          <div className={styles.sectionDescription}>
            {`Worst case: Σ NRI ${interestPercent(
              stats.maxNri
            )}, Σ WI ${interestPercent(stats.maxWi)}.`}
          </div>
        )}
        {status === 'complete' && elapsed !== null && (
          <div className={styles.sectionDescription}>
            {`Audit finished in ${elapsed}s.`}
          </div>
        )}
      </div>

      {!hasRun && (
        <InlineNotification
          kind="info"
          title="Not run yet"
          subtitle="Choose a grain and run the audit to build the report."
          hideCloseButton
        />
      )}

      {/* Says which of the two waits this is, so a busy queue is not mistaken
          for a slow report. The scan itself is a few seconds. */}
      {status === 'queued' && (
        <InlineNotification
          kind="info"
          title="Queued"
          subtitle="Waiting for a background worker to pick the audit up. This tier shares a dyno with Elasticsearch reindex work, so it can sit for a few minutes before it starts — the scan itself takes seconds."
          hideCloseButton
        />
      )}

      {error && (
        <InlineNotification
          kind="error"
          title="Error"
          subtitle={error}
          hideCloseButton
        />
      )}

      {phaseErrors.slots && (
        <InlineNotification
          kind="warning"
          title="Roll-up failed"
          subtitle={`The slot roll-up did not complete, so no results are shown: ${phaseErrors.slots}`}
          hideCloseButton
        />
      )}

      {/* The list survived but classification did not, so Category reads "--"
          throughout. Said plainly, because a blank column otherwise looks like
          every slot matched neither mechanism. */}
      {phaseErrors.categories && (
        <InlineNotification
          kind="info"
          title="Categories unavailable"
          subtitle={`Classification did not complete, so the Category column is unknown. The slots below are still accurate: ${phaseErrors.categories}`}
          hideCloseButton
        />
      )}

      {capped && (
        <InlineNotification
          kind="warning"
          title="Results truncated"
          subtitle="More slots are over 100% than this report returns. Only the worst offenders are listed."
          hideCloseButton
        />
      )}

      {stats && (
        <StatsBar
          stats={[
            {
              label: 'Slots Over 100%',
              value: stats.totalSlots,
              tagType: 'red',
            },
            { label: 'LTD', value: stats.ltdSlots, tagType: 'teal' },
            { label: 'Well', value: stats.wellSlots, tagType: 'blue' },
            {
              label: 'Excess Over 5%',
              value: stats.severe,
              tagType: 'magenta',
            },
            { label: 'Units Affected', value: stats.units, tagType: 'gray' },
            { label: 'Accounts', value: stats.accounts, tagType: 'gray' },
          ]}
        />
      )}

      {/* Only once classification has landed — before that every count is zero
          and the chips would read as "no slot carries any mechanism". */}
      {slots.some((row) => row.categories !== null) && (
        <div className={styles.categoryFilter}>
          {CATEGORY_FILTERS.map((filter) => {
            const total = slots.filter(filter.matches).length;
            const selected = activeCategories.has(filter.key);
            return (
              <Tag
                key={filter.key}
                type={selected ? filter.tagType : 'outline'}
                size="md"
                filter={selected}
                onClick={() => toggleCategory(filter.key)}
                onClose={() => toggleCategory(filter.key)}
                title="Clear filter"
              >
                {`${filter.label}: ${total}`}
              </Tag>
            );
          })}
          {activeCategories.size > 0 && (
            <Button
              kind="ghost"
              size="sm"
              onClick={() => setActiveCategories(new Set())}
            >
              Clear filters
            </Button>
          )}
        </div>
      )}

      {status === 'running' && slots.length === 0 && (
        <DataTableSkeleton columnCount={12} rowCount={10} />
      )}

      {slots.length > 0 && (
        <DataTable
          data={visibleSlots}
          columns={columns as ColumnDef<OverAllocatedSlot>[]}
          getRowId={(row) => row.key}
          persistenceKey="admin-over-allocated-slots"
          scroll={{ mode: 'paginated' }}
          renderRowSubComponent={renderRowSubComponent}
          {...TABLE_CONFIG}
        />
      )}
    </>
  );
};
